import { formatAppDateTime, formatAppTime } from "./appTimeFormat";

export const normalizeOutcomeLabel = (raw) =>
  String(raw || "outcome")
    .replace(/\./g, " · ")
    .replace(/_/g, " ")
    .replace(/\s+/g, " ")
    .trim();

export const formatOddsValue = (value) => {
  const numeric = Number(value);
  if (Number.isFinite(numeric)) return numeric.toFixed(2);
  return value == null || value === "" ? "—" : String(value);
};

export const formatOddsChangedAt = (value, { short = false, compact = false } = {}) => {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  if (compact) {
    const datePart = d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
    return `${datePart} · ${formatAppTime(d, { includeSeconds: false })}`;
  }
  return formatAppDateTime(d, { includeWeekday: !short });
};

export const getOddsMove = (entry) => {
  const oldValue = Number(entry?.oldOdds);
  const newValue = Number(entry?.newOdds);
  if (!Number.isFinite(oldValue) || !Number.isFinite(newValue)) {
    return { label: "Changed", tone: "neutral" };
  }
  if (newValue > oldValue) return { label: "Moved up", tone: "up" };
  if (newValue < oldValue) return { label: "Moved down", tone: "down" };
  return { label: "No move", tone: "flat" };
};

export const groupOddsHistory = (rows = []) => {
  const map = new Map();
  for (const row of rows) {
    const key = String(row?.outcome || "unknown");
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  }

  const groups = Array.from(map.entries()).map(([outcomeKey, entries]) => {
    const sorted = [...entries].sort(
      (a, b) =>
        new Date(b.changedAt || 0).getTime() - new Date(a.changedAt || 0).getTime()
    );
    const latest = sorted[0] || {};
    const upMoves = sorted.filter(
      (e) => Number(e.newOdds || 0) > Number(e.oldOdds || 0)
    ).length;
    const downMoves = sorted.filter(
      (e) => Number(e.newOdds || 0) < Number(e.oldOdds || 0)
    ).length;
    return {
      outcomeKey,
      outcomeLabel: normalizeOutcomeLabel(outcomeKey),
      latest,
      entries: sorted,
      count: sorted.length,
      upMoves,
      downMoves,
    };
  });

  groups.sort(
    (a, b) =>
      new Date(b.latest?.changedAt || 0).getTime() -
      new Date(a.latest?.changedAt || 0).getTime()
  );

  return groups;
};

export const buildOddsHistorySummary = (rows = [], groupedOutcomes = []) => {
  const latest = rows.reduce((max, row) => {
    const time = new Date(row?.changedAt || 0).getTime();
    return time > max ? time : max;
  }, 0);

  const upMoves = groupedOutcomes.reduce((sum, group) => sum + group.upMoves, 0);
  const downMoves = groupedOutcomes.reduce((sum, group) => sum + group.downMoves, 0);

  let bias = "balanced";
  if (upMoves > downMoves) bias = "rising";
  else if (downMoves > upMoves) bias = "dropping";

  return {
    totalChanges: rows.length,
    markets: groupedOutcomes.length,
    upMoves,
    downMoves,
    bias,
    latestChangedAt: latest
      ? formatOddsChangedAt(latest, { compact: true })
      : "No changes yet",
  };
};
